import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { CATEGORY_META } from "@/lib/constants";
import { renderToStaticMarkup } from "react-dom/server";

function pinIcon(category) {
  const meta = CATEGORY_META[category] || CATEGORY_META.mechanics;
  const { Icon, color } = meta;
  const svg = renderToStaticMarkup(<Icon size={15} color="#0B0F17" strokeWidth={2.5} />);
  return L.divIcon({
    className: "",
    html: `<div class="motor-pin" style="width:32px;height:32px;background:${color}"><div style="display:flex">${svg}</div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -30],
  });
}

export const MapView = ({ providers = [], onSelect, height = "h-[520px]" }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([53.0, -1.8], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = [];
    providers.forEach((p) => {
      if (p.lat == null || p.lng == null) return;
      const marker = L.marker([p.lat, p.lng], { icon: pinIcon(p.category) });
      const tierLabel = (p.tier || "free").toUpperCase();
      marker.bindPopup(
        `<div style="min-width:180px">
          <div style="font-weight:800;font-size:15px;font-family:'Barlow Condensed',sans-serif">${p.business_name}</div>
          <div style="color:#94A3B8;font-size:12px;margin:2px 0">${p.city} • ${tierLabel}</div>
          <div style="color:#F59E0B;font-size:13px;font-weight:600">★ ${p.rating || "New"} ${p.review_count ? "(" + p.review_count + ")" : ""}</div>
        </div>`
      );
      marker.on("click", () => onSelect && onSelect(p));
      marker.addTo(layer);
      bounds.push([p.lat, p.lng]);
    });
    if (bounds.length > 0) {
      map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 11, duration: 0.6 });
    }
    setTimeout(() => map.invalidateSize(), 100);
  }, [providers, onSelect]);

  return (
    <div
      data-testid="map-container"
      ref={containerRef}
      className={`${height} w-full rounded-2xl overflow-hidden border border-slate-800 shadow-2xl relative z-0`}
    />
  );
};
